import { Search } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useState, useEffect, useCallback } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getRazorPayInvoices, getStripeInvoices } from 'wisestep-api';
import {
  getDateUsingFormat,
  Label,
  ScrollableTabs,
  WsButton,
  WsColors,
  WsDatePicker,
  WsIconText,
  WsSelect,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';

import MyPlanLayout from './MyPlanLayout';
import RechargeInvoices from './RechargeInvoices';

interface IInvoicePaginateItem {
  first: string | null;
  last: string | null;
  current: 'first' | 'second' | null;
}

interface IInvoiceResponse {
  id: string;
  createdOn: string;
  description: string;
  amount: string;
  status: string;
  invoiceNumber: string;
  invoiceURL: string;
  totalCount: string;
}

interface IRazorPayInvoiceLineItems {
  description: string | null;
  name: string | null;
}

interface RazorPayInvoiceResponse {
  id: string;
  name: string;
  created_at: number;
  description: string;
  amount: string;
  status: string;
  invoice_number: string;
  short_url: string;
  totalCount: string;
  line_items: IRazorPayInvoiceLineItems[];
}

const Invoices = () => {
  const methods = useForm({
    defaultValues: {
      status: 'all',
    },
  });
  const workspace = useWorkspaceContext();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [razorpayRows, setRazorpayRows] = useState<Record<string, unknown>[]>([]);
  const [filterRows, setFilterRows] = useState<Record<string, unknown>[]>([]);
  const [isRowSet, setIsRowSet] = useState(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [invoiceItem, setInvoiceItem] = useState<IInvoicePaginateItem>({
    first: null,
    last: null,
    current: null,
  });
  const navigate = useNavigate();

  const statusArray = workspace.razorpaySubscriptionId
    ? [
        { label: 'All Invoices', value: 'all' },
        { label: 'Drafted Invoices', value: 'draft' },
        { label: 'Issued Invoices', value: 'issued' },
        { label: 'Partially Paid Invoices', value: 'partially_paid' },
        { label: 'Paid Invoices', value: 'paid' },
        { label: 'Cancelled Invoices', value: 'cancelled' },
        { label: 'Expired Invoices', value: 'expired' },
        { label: 'Deleted Invoices', value: 'deleted' },
      ]
    : [
        { label: 'All Invoices', value: 'all' },
        { label: 'Drafted Invoices', value: 'draft' },
        { label: 'Outstanding Invoices', value: 'open' },
        { label: 'Paid Invoices', value: 'paid' },
      ];
  const columns: GridColDef[] = workspace.razorpaySubscriptionId
    ? [
        {
          field: 'createdOn',
          headerName: 'Date',
          width: 150,
        },
        {
          field: 'description',
          headerName: 'Description',
          width: 330,
        },
        {
          field: 'amount',
          headerName: 'Amount',
        },
        {
          field: 'status',
          headerName: 'Status',
        },
        {
          field: 'invoiceURL',
          headerName: 'Actions',
          renderCell: (params) => (
            <WsIconText
              text=""
              textCursor="pointer"
              onClick={() => navigate(`/app-settings/my-invoices/${params.row.invoiceURL}`)}
              icon={<DownloadIcon sx={{ color: WsColors.TheBlues500 }} />}
            />
          ),
        },
      ]
    : [
        {
          field: 'createdOn',
          headerName: 'Date',
          width: 150,
        },
        {
          field: 'description',
          headerName: 'Description',
          width: 330,
        },
        {
          field: 'amount',
          headerName: 'Amount',
        },
        {
          field: 'status',
          headerName: 'Status',
        },
        {
          field: 'invoiceNumber',
          headerName: 'Invoice Number',
          width: 150,
        },
        {
          field: 'invoiceURL',
          headerName: 'Actions',
          renderCell: (params) => (
            <WsIconText
              text=""
              textCursor="pointer"
              onClick={() => window.open(params.row.invoiceURL, '_blank')}
              icon={<DownloadIcon sx={{ color: WsColors.TheBlues500 }} />}
            />
          ),
        },
      ];
  const setData = useCallback(
    (resData: IInvoiceResponse[]) => {
      const tempRows: Record<string, unknown>[] = [];
      let rowCount = 0;
      const tempInvoiceItem = { ...invoiceItem };
      resData.map((data, index) => {
        if (index === 0) {
          tempInvoiceItem.first = data.id;
          rowCount = parseInt(data.totalCount);
        } else if (index === resData.length - 1) {
          tempInvoiceItem.last = data.id;
        }
        tempRows.push({
          id: index,
          createdOn: dayjs.unix(parseInt(data.createdOn)).format('DD MMM YYYY HH:mm'),
          description: data.description,
          amount: `$ ${data.amount}`,
          status: data.status,
          invoiceNumber: data.invoiceNumber,
          invoiceURL: data.invoiceURL,
        });
      });
      setInvoiceItem(tempInvoiceItem);
      setRowCount(rowCount);
      setRows(tempRows);
    },
    [invoiceItem]
  );

  const setRazorPayDescription = (lineItem: IRazorPayInvoiceLineItems[]) => {
    let description = '';
    if (lineItem.length > 0 && !isEmpty(lineItem[0].name) && lineItem[0]?.name) {
      description =
        lineItem[0].name === 'Update Request'
          ? 'Current plan quantity update request'
          : lineItem[0].name;
    }
    return description;
  };

  const setRazorPayData = useCallback(
    (resData: RazorPayInvoiceResponse[]) => {
      const tempRows: Record<string, unknown>[] = [];
      const tempInvoiceItem = { ...invoiceItem };
      resData.map((data, index) => {
        if (index === 0) {
          tempInvoiceItem.first = data.id;
        } else if (index === resData.length - 1) {
          tempInvoiceItem.last = data.id;
        }
        tempRows.push({
          id: index,
          createdOn: dayjs.unix(data.created_at ?? '').format('DD MMM YYYY'),
          description: !isEmpty(data.line_items)
            ? setRazorPayDescription(data.line_items)
            : data.description,
          amount: `Rs ${Number(data.amount) / 100}`,
          status: data.status,
          invoiceNumber: data.invoice_number,
          invoiceURL: data.id,
        });
      });
      setInvoiceItem(tempInvoiceItem);
      setRazorpayRows(tempRows);
      setFilterRows(tempRows);
    },
    [invoiceItem]
  );
  useEffect(() => {
    if (!isRowSet) {
      (async () => {
        if (workspace?.razorpaySubscriptionId) {
          const res = await getRazorPayInvoices(workspace.razorpaySubscriptionId);
          setRowCount(res.data.count);
          setRazorPayData(res.data.items);
          setIsRowSet(true);
        } else if (workspace?.stripeCustomerId) {
          const res = await getStripeInvoices({
            startingAfter: null,
            endingBefore: null,
            fromDate: null,
            toDate: null,
            status: null,
            query: null,
          });
          setData(res.data);
          setIsRowSet(true);
        }
      })();
    }
  }, [
    isRowSet,
    setData,
    setRazorPayData,
    workspace.razorpaySubscriptionId,
    workspace.stripeCustomerId,
  ]);

  const onPagination = async (page: number) => {
    const res = await getStripeInvoices({
      startingAfter: currentPage < page ? invoiceItem.last : null,
      endingBefore: currentPage > page ? invoiceItem.first : null,
      fromDate: null,
      toDate: null,
      status: null,
      query: null,
    });
    const tempInvoiceItem = { ...invoiceItem };
    tempInvoiceItem.current = currentPage < page ? 'first' : 'second';
    setInvoiceItem(tempInvoiceItem);
    setData(res.data);
    setCurrentPage(page);
  };

  const stripeFilter = async (data?: FieldValues) => {
    const res = await getStripeInvoices({
      startingAfter: invoiceItem.current === 'first' ? invoiceItem.last : null,
      endingBefore: invoiceItem.current === 'second' ? invoiceItem.first : null,
      fromDate: data?.fromDate
        ? parseInt(
            (
              new Date(
                data.fromDate.getFullYear(),
                data.fromDate.getMonth(),
                data.fromDate.getDate()
              ).getTime() / 1000
            ).toFixed(0)
          ).toString()
        : null,
      toDate: data?.fromDate
        ? parseInt(
            (
              new Date(
                data.fromDate.getFullYear(),
                data.fromDate.getMonth(),
                data.fromDate.getDate()
              ).getTime() /
                1000 +
              24 * 60 * 60
            ).toFixed(0)
          ).toString()
        : null,
      status: data?.status && data?.status !== 'all' ? data.status : null,
      query: data?.query,
    });
    setData(res.data);
  };
  const razorPayFileter = (data?: FieldValues) => {
    if (!isEmpty(data)) {
      if (data?.status && data?.fromDate) {
        if (data?.status == 'all')
          setFilterRows(
            razorpayRows.filter(
              (v) => v.createdOn == getDateUsingFormat(data?.fromDate, 'DD MMM YYYY')
            )
          );
        else
          setFilterRows(
            razorpayRows.filter(
              (v) =>
                v.status == data.status &&
                v.createdOn == getDateUsingFormat(data?.fromDate, 'DD MMM YYYY')
            )
          );
      } else if (data?.status) {
        if (data?.status == 'all') setFilterRows(razorpayRows);
        else setFilterRows(razorpayRows.filter((v) => v.status == data.status));
      } else if (data?.fromDate) {
        setFilterRows(
          razorpayRows.filter(
            (v) => v.createdOn == getDateUsingFormat(data?.fromDate, 'DD MMM YYYY')
          )
        );
      }
    } else {
      setFilterRows(razorpayRows);
    }
  };
  const onSubmit = async (data?: FieldValues) => {
    if (rows.length > 0) {
      stripeFilter(data);
    } else if (razorpayRows.length > 0) {
      razorPayFileter(data);
    }
  };

  return (
    <MyPlanLayout>
      <Box mb={2} sx={{ ml: '-20px' }}>
        <ScrollableTabs
          scrollList={[{ tabName: 'Subscriptions' }, { tabName: 'Recharges' }]}
          getTabIndex={(index: number) => setTabIndex(index)}
        />
      </Box>
      {tabIndex === 0 ? (
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Label>Select Invoice status</Label>
                <WsSelect name="status" size="small" selectArray={statusArray} />
              </Grid>
              <Grid item xs={4}>
                <Label>Created on</Label>
                <WsDatePicker name="fromDate" size="small" label="" disableFuture={true} />
              </Grid>
              <Grid item xs={2} mt={3.5}>
                <Box sx={{ float: 'right' }}>
                  <WsButton
                    variant="outlined"
                    onClick={() => {
                      methods.reset();
                      onSubmit();
                    }}
                    size="large"
                  >
                    Reset
                  </WsButton>
                </Box>
              </Grid>
              <Grid item xs={2} mt={3.5}>
                <WsButton variant="outlined" type="submit" size="large" startIcon={<Search />}>
                  Submit
                </WsButton>
              </Grid>
            </Grid>
            <Box sx={{ height: '670px', mt: 2 }}>
              <DataGrid
                rows={rows.length > 0 ? rows : filterRows}
                columns={columns}
                rowCount={rowCount}
                onPageChange={onPagination}
                paginationMode="server"
                rowsPerPageOptions={[2, 3, 4]}
                pageSize={10}
              />
            </Box>
          </Box>
        </FormProvider>
      ) : (
        <RechargeInvoices />
      )}
    </MyPlanLayout>
  );
};

export default Invoices;
